import React, { useState, useEffect } from "react";
import { db } from "./firebase.js";
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs } from "firebase/firestore";

function RentalForm({ onSubmit, onCancel, initialData = {}, isSubmitting, isMobile }) {
  const [formData, setFormData] = useState({
    lotNumber: initialData.lotNumber || "",
    cabinName: initialData.cabinName || "",
    rentalLink: initialData.rentalLink || "",
    alternateRentalLink: initialData.alternateRentalLink || "",
    picture: initialData.picture || "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="modal-overlay">
      <div className={`modal-content ${isMobile ? 'modal-content-mobile' : ''}`}>
        <h2 className="modal-title">{initialData.lotNumber ? "Edit Rental" : "Add Rental"}</h2>
        <form onSubmit={handleSubmit}>
          <div className={`form-grid ${isMobile ? 'form-grid-mobile' : ''}`}>
            <div className="form-group">
              <label className="form-label">Lot Number</label>
              <input
                type="text"
                name="lotNumber"
                value={formData.lotNumber}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Cabin Name</label>
              <input
                type="text"
                name="cabinName"
                value={formData.cabinName}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Rental Link</label>
              <input
                type="url"
                name="rentalLink"
                value={formData.rentalLink}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Alternate Rental Link</label>
              <input
                type="url"
                name="alternateRentalLink"
                value={formData.alternateRentalLink}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group full-width">
              <label className="form-label">Picture URL</label>
              <input
                type="url"
                name="picture"
                value={formData.picture}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
          </div>
          <div className="modal-buttons">
            <button
              type="button"
              onClick={onCancel}
              className="cancel-button"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="save-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const RentalsPage = ({ user, isAdmin, isMobile }) => {
  const [rentals, setRentals] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editRental, setEditRental] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchRentals = async () => {
      setIsLoading(true);
      try {
        const rentalsCollection = collection(db, "rentals");
        const snapshot = await getDocs(rentalsCollection);
        if (isMounted) {
          const rentalsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setRentals(rentalsList);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Error fetching rentals:", err.message);
        if (isMounted) {
          setError("Failed to load rentals: " + err.message);
          setIsLoading(false);
        }
      }
    };

    fetchRentals();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleAddRental = async (newRental) => {
    if (!isAdmin) {
      setError("Only admins can add rentals.");
      return;
    }
    try {
      setError(null);
      setIsSubmitting(true);
      const rentalsCollection = collection(db, "rentals");
      await addDoc(rentalsCollection, newRental);
      setShowAddForm(false);
      const snapshot = await getDocs(rentalsCollection);
      const rentalsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRentals(rentalsList);
    } catch (err) {
      if (err.code === "permission-denied") {
        setError("Only admins can add rentals.");
      } else {
        setError("Failed to add rental: " + err.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditRental = async (updatedRental) => {
    if (!isAdmin) {
      setError("Only admins can edit rentals.");
      return;
    }
    try {
      setError(null);
      setIsSubmitting(true);
      const rentalDoc = doc(db, "rentals", editRental.id);
      await updateDoc(rentalDoc, updatedRental);
      setEditRental(null);
      const rentalsCollection = collection(db, "rentals");
      const snapshot = await getDocs(rentalsCollection);
      const rentalsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRentals(rentalsList);
    } catch (err) {
      if (err.code === "permission-denied") {
        setError("Only admins can edit rentals.");
      } else {
        setError("Failed to edit rental: " + err.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRental = async (rentalId) => {
    if (!isAdmin) {
      setError("Only admins can delete rentals.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this rental?")) {
      try {
        setError(null);
        setIsSubmitting(true);
        const rentalDoc = doc(db, "rentals", rentalId);
        await deleteDoc(rentalDoc);
        const rentalsCollection = collection(db, "rentals");
        const snapshot = await getDocs(rentalsCollection);
        const rentalsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setRentals(rentalsList);
      } catch (err) {
        if (err.code === "permission-denied") {
          setError("Only admins can delete rentals.");
        } else {
          setError("Failed to delete rental: " + err.message);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="page-container">
      <style>
        {`
          .page-container {
            padding: 2rem;
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            position: relative;
            z-index: 1;
          }
          .page-title {
            font-size: 1.5rem;
            margin-bottom: 1rem;
            color: white;
          }
          .error-message {
            background: rgba(255, 0, 0, 0.1);
            padding: 1rem;
            margin-bottom: 1rem;
            border-radius: 0.5rem;
            color: #991b1b;
          }
          .add-button {
            background: #1a3c34;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
          }
          .add-button:hover {
            background: #14532d;
          }
          .add-button:disabled {
            background: #6b7280;
          }
          .table-container {
            overflow-x: auto;
            position: relative;
            z-index: 1;
            margin-top: 1rem;
          }
          .rental-table {
            min-width: 100%;
            border-collapse: collapse;
            background: rgba(255, 255, 255, 0.8);
            backdrop-filter: blur(5px);
          }
          .table-header {
            background: #1a3c34;
            color: white;
          }
          .table-header-cell {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            text-align: left;
            white-space: nowrap;
          }
          .table-row:nth-child(even) {
            background: rgba(243, 244, 246, 0.8);
          }
          .table-row:nth-child(odd) {
            background: rgba(229, 231, 235, 0.8);
          }
          .table-cell {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            white-space: nowrap;
          }
          .rental-link {
            color: #3b82f6;
            text-decoration: none;
          }
          .rental-link:hover {
            text-decoration: underline;
          }
          .rental-image {
            width: 150px;
            height: auto;
            border-radius: 0.25rem;
          }
          .action-buttons {
            display: flex;
            gap: 0.5rem;
          }
          .edit-button {
            background: #3b82f6;
            color: white;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
          }
          .edit-button:hover {
            background: #2563eb;
          }
          .edit-button:disabled {
            background: #93c5fd;
          }
          .delete-button {
            background: #ef4444;
            color: white;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
          }
          .delete-button:hover {
            background: #dc2626;
          }
          .delete-button:disabled {
            background: #f87171;
          }
          .loading-message,
          .no-rentals {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            text-align: center;
          }
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }
          .modal-content {
            background: white;
            padding: 1.5rem;
            border-radius: 0.5rem;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
            width: 90%;
            max-width: 800px;
            z-index: 1001;
          }
          .modal-title {
            font-size: 1.25rem;
            margin-bottom: 1rem;
          }
          .form-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
          }
          .form-group {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
          .form-label {
            font-size: 0.875rem;
            font-weight: 500;
          }
          .form-input {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            border-radius: 0.25rem;
            width: 100%;
            font-size: 1rem;
          }
          .full-width {
            grid-column: span 2;
          }
          .modal-buttons {
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
            margin-top: 1rem;
          }
          .cancel-button {
            background: #6b7280;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
          }
          .cancel-button:hover {
            background: #4b5563;
          }
          .cancel-button:disabled {
            background: #9ca3af;
          }
          .save-button {
            background: #3b82f6;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
          }
          .save-button:hover {
            background: #2563eb;
          }
          .save-button:disabled {
            background: #93c5fd;
          }
          @media (max-width: 640px) {
            .page-container {
              padding: 1rem;
            }
            .page-title {
              font-size: 1.25rem;
            }
            .error-message {
              font-size: 0.875rem;
              padding: 0.75rem;
            }
            .add-button {
              padding: 0.4rem 0.8rem;
              font-size: 0.9rem;
            }
            .table-header-cell,
            .table-cell {
              padding: 0.4rem;
              font-size: 0.875rem;
            }
            .edit-button,
            .delete-button {
              padding: 0.25rem 0.5rem;
              font-size: 0.875rem;
            }
            .loading-message,
            .no-rentals {
              font-size: 0.875rem;
              padding: 0.4rem;
            }
            .modal-content-mobile {
              padding: 1rem;
              max-width: 350px;
            }
            .modal-title {
              font-size: 1rem;
            }
            .form-grid-mobile {
              grid-template-columns: 1fr;
            }
            .form-label {
              font-size: 0.75rem;
            }
            .form-input {
              font-size: 0.9rem;
              padding: 0.4rem;
            }
            .cancel-button,
            .save-button {
              padding: 0.4rem 0.8rem;
              font-size: 0.9rem;
            }
            .rental-image {
              width: 100px;
            }
          }
        `}
      </style>
      <h1 className="page-title">Rental Properties</h1>
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      {isAdmin && (
        <button
          onClick={() => setShowAddForm(true)}
          className="add-button"
          disabled={isSubmitting}
        >
          Add New Rental
        </button>
      )}
      {showAddForm && (
        <RentalForm
          onSubmit={handleAddRental}
          onCancel={() => setShowAddForm(false)}
          isSubmitting={isSubmitting}
          isMobile={isMobile}
        />
      )}
      {editRental && (
        <RentalForm
          onSubmit={handleEditRental}
          onCancel={() => setEditRental(null)}
          initialData={editRental}
          isSubmitting={isSubmitting}
          isMobile={isMobile}
        />
      )}
      <div className="table-container">
        <table className="rental-table">
          <thead className="table-header">
            <tr>
              <th className="table-header-cell">Lot Number</th>
              <th className="table-header-cell">Cabin Name</th>
              <th className="table-header-cell">Rental Link</th>
              <th className="table-header-cell">Alternate Rental Link</th>
              <th className="table-header-cell">Picture</th>
              {isAdmin && <th className="table-header-cell">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={isAdmin ? "6" : "5"} className="loading-message">
                  Loading rentals...
                </td>
              </tr>
            ) : rentals.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? "6" : "5"} className="no-rentals">
                  {error ? "Failed to load rentals." : "No rentals found."}
                </td>
              </tr>
            ) : (
              rentals.map((rental) => (
                <tr key={rental.id} className="table-row">
                  <td className="table-cell">{rental.lotNumber}</td>
                  <td className="table-cell">{rental.cabinName}</td>
                  <td className="table-cell">
                    <a href={rental.rentalLink} target="_blank" rel="noopener noreferrer" className="rental-link">
                      {rental.rentalLink}
                    </a>
                  </td>
                  <td className="table-cell">
                    <a href={rental.alternateRentalLink} target="_blank" rel="noopener noreferrer" className="rental-link">
                      {rental.alternateRentalLink || 'N/A'}
                    </a>
                  </td>
                  <td className="table-cell">
                    <img
                      src={rental.picture || "https://via.placeholder.com/150?text=No+Image"}
                      alt={`${rental.cabinName} cabin`}
                      className="rental-image"
                    />
                  </td>
                  {isAdmin && (
                    <td className="table-cell">
                      <div className="action-buttons">
                        <button
                          onClick={() => setEditRental(rental)}
                          className="edit-button"
                          disabled={isSubmitting}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteRental(rental.id)}
                          className="delete-button"
                          disabled={isSubmitting}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RentalsPage;